import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormHelperText, Typography } from '@mui/material';
import { MobileDateTimePicker, TimeView } from '@mui/x-date-pickers';
import { add, isSameHour, roundToNearestHours } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Select } from 'src/components/molecules/select';
import { useBlockSpace } from 'src/features/spaces/api/hooks/use-block-space';
import { BlockSpacePayload } from 'src/features/spaces/api/types/payload/block-space.payload';
import { SpaceUnavailabilityState } from 'src/features/spaces/api/types/response/space.response';
import { getDateIsoStringWithoutSecondsAndMilliseconds } from 'src/utils/get-date-iso-string-without-seconds-and-milliseconds';
import { z } from 'zod';
import { useBlockSpaceDialogFormValidationSchema } from './schema/use-form-validation-schema';

interface Props extends DialogActions {
  spaceToken: string;
  spaceBlockedStates: SpaceUnavailabilityState[];
}

export const BlockSpaceDialog = ({
  open,
  onCancel,
  spaceToken,
  spaceBlockedStates,
}: Props) => {
  const { t } = useTranslation(['spaces', 'common']);

  const formValidationSchema = useBlockSpaceDialogFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    type: 'permanent',
    from: null,
    to: null,
  };

  const { control, watch, reset, handleSubmit } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
  });

  const handleCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: blockSpace, isPending: isBlockingSpace } =
    useBlockSpace(handleCancel);

  const handleBlockSpace = (data: FormValues) => {
    let payload: BlockSpacePayload | null = null;

    if (data.type === 'permanent') {
      payload = {
        from: getDateIsoStringWithoutSecondsAndMilliseconds(
          roundToNearestHours(new Date(), {
            nearestTo: 1,
            roundingMethod: 'floor',
          }),
        ),
        until: null,
      };
    }

    if (data.type === 'specificDateAndTime' && data.from && data.to) {
      payload = {
        from: getDateIsoStringWithoutSecondsAndMilliseconds(data.from),
        until: getDateIsoStringWithoutSecondsAndMilliseconds(data.to),
      };
    }

    if (payload) {
      blockSpace({
        spaceToken,
        payload,
      });
    }
  };

  const shouldDisableHour = (date: Date, _view: TimeView) => {
    if (isSameHour(new Date(), date)) {
      return true;
    }

    return false;
  };

  const type = watch('type');

  const from = watch('from');

  const selectTypeOptions = [
    {
      label: t('spaces:common.blockSpaceDialog.selectType.permanent'),
      value: 'permanent',
    },
    {
      label: t('spaces:common.blockSpaceDialog.selectType.specificDateAndTime'),
      value: 'specificDateAndTime',
    },
  ];

  const spaceHasAnyFutureOrPendingHourlyBlocks = spaceBlockedStates.some(
    item =>
      item.state === 'BLOCKED' && Date.now() < item.until && !item.isPermanent,
  );

  return (
    <Dialog
      title={t('spaces:common.blockSpaceDialog.title')}
      open={open}
      onCancel={handleCancel}
      onConfirm={handleSubmit(handleBlockSpace)}
      isConfirmButtonSubmitting={isBlockingSpace}
      isConfirmButtonDisabled={
        spaceHasAnyFutureOrPendingHourlyBlocks && type === 'permanent'
      }
      confirmButtonTooltip={t(
        'spaces:common.blockSpaceDialog.confirmButtonTooltip',
      )}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          control={control}
          name="type"
          render={({
            field: { name, value, onChange, onBlur },
            fieldState: { error, invalid },
          }) => (
            <Select
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={invalid}
              helperText={error?.message}
              label={t('spaces:common.blockSpaceDialog.selectType.label')}
              items={selectTypeOptions}
            />
          )}
        />

        <Box>
          <Typography
            variant="body2"
            color={type === 'permanent' ? 'textSecondary' : 'textPrimary'}>
            {t('common.blockSpaceDialog.selectDateAndTime')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            control={control}
            name="from"
            render={({
              field: { name, value, onChange },
              fieldState: { error, invalid },
            }) => (
              <Box sx={{ flex: 1 }}>
                <MobileDateTimePicker
                  name={name}
                  value={value}
                  onChange={onChange}
                  ampm={false}
                  disabled={type === 'permanent'}
                  minDateTime={new Date()}
                  sx={{ width: '100%' }}
                  views={['day', 'hours']}
                  format="dd/MM/yyyy HH:mm"
                  shouldDisableTime={shouldDisableHour}
                />

                {invalid ? (
                  <FormHelperText error>{error?.message}</FormHelperText>
                ) : null}
              </Box>
            )}
          />

          <Controller
            control={control}
            name="to"
            render={({
              field: { name, value, onChange },
              fieldState: { error, invalid },
            }) => (
              <Box sx={{ flex: 1 }}>
                <MobileDateTimePicker
                  name={name}
                  value={value}
                  onChange={onChange}
                  ampm={false}
                  disabled={type === 'permanent'}
                  minDateTime={
                    from ? add(new Date(from), { hours: 1 }) : new Date()
                  }
                  sx={{ width: '100%' }}
                  views={['day', 'hours']}
                  format="dd/MM/yyyy HH:mm"
                />

                {invalid ? (
                  <FormHelperText error>{error?.message}</FormHelperText>
                ) : null}
              </Box>
            )}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
